import React, { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query"

import CloseIcon from "@mui/icons-material/Close"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { SelectWithSearch } from "../../../components/form/SelectWithSearch/SelectWithSearch"
import { KonanAPI } from "../../../services/KonanAPI"
import type { Model } from "../../../types/generated/api/Model"
import type { ModelListCreate } from "../../../types/generated/api/ModelListCreate"
import type { PaginatedModelListCreateList } from "../../../types/generated/api/PaginatedModelListCreateList"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"

type Props = {
  open: boolean
  onClose: () => void
  projectId: string
  trainingDataId: string
  trainingName: string
}

export function AssignTrainingDataToModelDialog(props: Readonly<Props>): React.ReactElement {
  const { open, onClose, projectId, trainingDataId, trainingName } = props

  const { t } = useTranslation("TrainingData")
  const queryClient = useQueryClient()

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastItemRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isModelsLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const [selectedModel, setSelectedModel] = useState<{
    value: string
    text: string
  }>({
    value: "",
    text: "",
  })

  const [modelsSearchValue, setModelsSearchValue] = useState<string | undefined>(undefined)
  const [replaceTrainingDataWarning, setReplaceTrainingDataWarning] = useState<boolean>(false)

  const DEFAULT_TRAINING_DATA_NAME = t("training_data_card.defaultFileName")

  const {
    data: models,
    isLoading: isModelsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError, ModelListCreate>(
    ["models", projectId, modelsSearchValue, "paginated", "state"],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchModels({
        projectId: projectId,
        page: pageParam,
        search: modelsSearchValue,
        ordering: "state",
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ModelListCreate[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ModelListCreate[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      refetchOnMount: true,
      enabled: !!projectId,
    },
  )

  const { data: modelData } = useQuery<AxiosResponse<Model>, AxiosError>(
    ["model", selectedModel],
    () => KonanAPI.fetchModel(selectedModel.value),
    { enabled: selectedModel.value !== "" },
  )

  const AssignedModelMutation = useMutation<AxiosResponse>(() =>
    KonanAPI.AssignTrainingDataToModel(selectedModel.value, trainingDataId),
  )

  async function AssignOrReplaceModelToTrainingData(): Promise<void> {
    try {
      await AssignedModelMutation.mutateAsync()
      // Invalidate react-query queries
      await queryClient.invalidateQueries("models")
      await queryClient.invalidateQueries(["training-data", projectId])
      onClose()

      const displayTraining = trainingName || DEFAULT_TRAINING_DATA_NAME
      const displayModel = modelData?.data?.name || t("assign_dialog.notifications.defaultModelName")
      NotificationUtils.toast(
        t("assign_dialog.notifications.assignSuccess", { trainingName: displayTraining, modelName: displayModel }),
        {
          severity: "success",
        },
      )

      window.scrollTo(0, 0)
    } catch (e) {
      onClose()
      const displayTraining = trainingName || DEFAULT_TRAINING_DATA_NAME
      const displayModel = modelData?.data?.name || t("assign_dialog.notifications.defaultModelName")
      NotificationUtils.toast(
        t("assign_dialog.notifications.assignError", { trainingName: displayTraining, modelName: displayModel }),
        {
          severity: "error",
        },
      )
    }
  }

  async function assignModel(type: string): Promise<void> {
    if (type === "Replace") {
      AssignOrReplaceModelToTrainingData()
    } else if (type === "Assign") {
      if (modelData?.data && modelData?.data?.training_data && modelData?.data?.training_data !== null) {
        setReplaceTrainingDataWarning(true)
      } else {
        setReplaceTrainingDataWarning(false)
        AssignOrReplaceModelToTrainingData()
      }
    }
  }

  useEffect(() => {
    if (open) {
      setReplaceTrainingDataWarning(false)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && AssignedModelMutation.isLoading) {
          return undefined
        } else {
          onClose()
        }
      }}
    >
      <DialogTitle className="alt-dialog-header-base">
        <Grid container justifyContent="space-between">
          <Typography variant="h2-bold">
            {replaceTrainingDataWarning ? t("assign_dialog.title.replace") : t("assign_dialog.title.assign")}
          </Typography>

          <IconButton aria-label="close" onClick={onClose} className="close-icon-button">
            <CloseIcon style={{ color: "var(--gray-background-1)" }} />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        <Typography variant="h3-bold" gutterBottom>
          {replaceTrainingDataWarning
            ? t("assign_dialog.subtitle.replace", {
                modelName: modelData?.data?.name ?? t("assign_dialog.subtitle.defaultThisModel"),
              })
            : t("assign_dialog.subtitle.assign")}
        </Typography>

        {!replaceTrainingDataWarning && (
          <Grid item xs={12}>
            <SelectWithSearch
              optionsList={
                models?.pages.map((model) => {
                  return {
                    text: model.name,
                    value: model.uuid,
                    item: <Typography variant="a">{model.name}</Typography>,
                    disabled: false,
                  }
                }) ?? []
              }
              isOptionsLoading={isModelsLoading}
              inputValue={selectedModel.text ?? t("assign_dialog.select.inputValue")}
              menuTriggerProps={{
                placeholder:
                  !models?.pages || models?.pages.length === 0
                    ? t("assign_dialog.select.noModels")
                    : t("assign_dialog.select.chooseModel"),
                label: t("assign_dialog.select.label"),
                fullWidth: true,
                hideDescription: true,
                disabled: isModelsLoading || !models?.pages || models?.pages.length === 0,
              }}
              onSelectMenuItem={({ value, text }) =>
                setSelectedModel({
                  value,
                  text,
                })
              }
              searchInputChangeHandler={(e: InputChangeEvent) => {
                setModelsSearchValue(e.target.value.toString())
              }}
              onMenuClose={() => setModelsSearchValue(undefined)}
              lastMenuItemRef={lastItemRef}
            />
          </Grid>
        )}

        {replaceTrainingDataWarning ? (
          <Typography variant="p">{t("assign_dialog.confirm.replace")}</Typography>
        ) : (
          <Typography variant="label-regular" style={{ marginTop: "4px" }}>
            {t("assign_dialog.note.replaced")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions className="alt-dialog-actions-base">
        <Button variant="secondary" onClick={onClose}>
          {t("assign_dialog.buttons.cancel")}
        </Button>
        <Button
          onClick={() => assignModel(replaceTrainingDataWarning ? "Replace" : "Assign")}
          disabled={!selectedModel || AssignedModelMutation.isLoading}
          variant="primary"
          isLoading={
            (replaceTrainingDataWarning && AssignedModelMutation.isLoading) ||
            (!replaceTrainingDataWarning && AssignedModelMutation.isLoading)
          }
        >
          {replaceTrainingDataWarning && !AssignedModelMutation.isLoading
            ? t("assign_dialog.buttons.replace")
            : t("assign_dialog.buttons.assign")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
